import React, {useState, useEffect, useCallback} from 'react';
import {duckify, singleDuck, dependentDuck, filterDuck, currentUser, currentCustomer} from "../../../utils/duckify";
import {billingAccounts, billingPayments, paymentProfiles} from "../../../ducks";
import OrderForm from '../../payments/OrderForm';
import Placeholder from '../../Placeholder';
import { formatCurrency, parseCurrency } from '../../../utils/i18n';
import SideBar from "../sidebar/SideBar";
import PaymentProfileSummary from "../sidebar/PaymentProfileSummary";
import SchoolContactInfo from "../sidebar/SchoolContactInfo";
import BillingAccountOverview from "./BillingAccountOverview";
import FeeList from "components/payments/FeeList";
import FontAwesome from "utils/FA";
import {faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import {faUsdCircle} from '@fortawesome/pro-solid-svg-icons/faUsdCircle';
import {formattedDate, titleize} from "../../../utils";
import './styles/billing_account.scss';
import debounce from 'lodash/debounce';
import {OverlayTrigger, Tooltip} from "react-bootstrap";
import rollbar from "../../../utils/rollbarLogger";


const BillingAccount = ({allDucksReady, billingPayment, billingAccount, currentCustomer, updatePaymentMethod, dispatch}) => {

  const feeConfig = billingPayment.convenience_fee && currentCustomer
    .payment_config
    .payment_methods[billingPayment.effective_payment_method]
    ?.convenience_fee;

  const [amount, setAmount] = useState(0);
  const [type, setType] = useState("");
  const [submit, setSubmit] = useState(false);
  const [convenienceFees, setConvenienceFees] = useState({});
  const billingPaymentAmount = billingPayment.amount > 0 ? billingPayment.amount : 0;

  // sets where we get our current amountDue from
  const getAmountDue = (type) => {
    switch (type) {
      case 'current_balance':
        return billingPaymentAmount;
      case 'upcoming_balance':
        return billingAccount.next_balance;
      case 'manual_payment':
        return amount ? parseCurrency(amount) : 0;
      default:
        return 0
    }
  };

  const amountDue = getAmountDue(type);


  const updateBillingPayment = (billingPayment, amountDue, convenienceFee, type) => {
    // Fires only on submit and updates the amount of the billing payment to reflect the entered amount
      setSubmit(true);
      billingPayment.amount = amountDue;
      billingPayment.convenience_fee = convenienceFee;
      billingPayment.amount_type = type;
      return(dispatch(billingPayments.actions.update(billingPayment)).catch((error) => {
        setSubmit(false)
        throw error
      }))
    };

  const getConvenienceFee = useCallback(debounce((currentAmount) => {
    const paymentMethod = billingPayment.effective_payment_method;
      const feesForMethod = convenienceFees[paymentMethod] || {};
      if (currentAmount > 0 && billingPayment && !Object.prototype.hasOwnProperty.call(feesForMethod, currentAmount)) {
        // Fetches a convenience fee based on the payment amount
        const token = $('meta[name="csrf-token"]').attr('content');

        fetch('/api/billing_payments/convenience_fee', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': token
          },
          body: JSON.stringify({amount: currentAmount, id: billingPayment.id})
        })
          .then((response) => response.json())
          .then((json) => {
            feesForMethod[currentAmount] = json.convenience_fee;
            setConvenienceFees({ ...convenienceFees, [paymentMethod]: feesForMethod });
          });
      }
    },
    500
  ),[billingPayment]);

  const convenienceFeeAmount = () => {
    const feeForMethod = convenienceFees[billingPayment.effective_payment_method] || {};
    return feeForMethod[amountDue] || 0;
  };

  useEffect(() => {
    if (billingPayment.convenience_fee) {
      getConvenienceFee(amountDue);
    }
  }, [amount, type, billingPayment]);

  const totalAmount = parseCurrency(convenienceFeeAmount()) + parseCurrency(amountDue);

  return <Placeholder.loading ready={allDucksReady} content={() =>
    <div className={'billing-account'}>
      <h2>
        <FontAwesome icon={faUsdCircle} nbsp/>
        Make a Payment
      </h2>
      <h3>Choose Payment Type</h3>

      {billingPaymentAmount > 0 && billingPayment.amount_type !== 'manual_payment' && <div className='billing-account-payment-section'>
        <input disabled={submit} name='billing-account-current-balance' type={'radio'} value={'current_balance'}
               checked={type === 'current_balance'} onClick={(e) => setType(e.target.value)}/>
        <label className='billing-account-label'>Current Balance</label>
        <span className='billing-account-label'>{formatCurrency(billingPaymentAmount)}</span>
      </div>}
      {billingAccount.next_balance > 0 && billingPaymentAmount <= 0 &&
      <div className='billing-account-payment-section'>
        <input disabled={submit} name='billing-account-upcoming-balance' type={'radio'} value={'upcoming_balance'}
               checked={type === 'upcoming_balance'} onClick={(e) => setType(e.target.value)}/>
        <label className='billing-account-label'>Upcoming Balance</label>
        <OverlayTrigger placement={'right'}
                        overlay={<Tooltip>
                          Due on {formattedDate(billingAccount.next_date)}
                        </Tooltip>}
        >
          <FontAwesome className='billing-account-info-icon' icon={faInfoCircle} preNbsp/>
        </OverlayTrigger>
        <span className='billing-account-label'>{formatCurrency(billingAccount.next_balance)}</span>
      </div>}

      <div className='billing-account-payment-section'>
        <input disabled={submit} className='billing-account-manual' type={'radio'} value={'manual_payment'}
               checked={type === 'manual_payment'} onClick={(e) => setType(e.target.value)}/>
        <label className='billing-account-label'> Enter Amount</label>
        <input type='number' placeholder='0.00' disabled={type !== 'manual_payment' || submit}
               className={'billing-account-input'} value={amount} name='billing-account-manual-amount'
               onChange={(e) => setAmount(e.target.value)}/>
      </div>

      <h3 className={'billing-title'}>Amount Due</h3>
      <FeeList
        fees={[
          { description: titleize(type), amount: amountDue || 0 },
          (!!billingPayment.convenience_fee) && {
            description: feeConfig?.charge_description,
            amount: convenienceFeeAmount(),
            className: 'convenience-fee'
          }
        ]}
      />

      <OrderForm
        order={{
          entity: billingPayment,
          entity_id: billingPayment.id,
          entity_class: 'BillingPayment',
        }}
        amount={totalAmount}
        beforeChange={(payment_method, payment_profile_id) => updatePaymentMethod({
          ...billingPayment,
          payment_method,
          payment_profile_id
        })}
        disabled={amountDue < 0.01}
        beforeSubmit={() => updateBillingPayment(billingPayment, amountDue, convenienceFeeAmount(), type)}
        convenienceFee={convenienceFeeAmount}
        paymentMethods={billingPayment.supported_payment_methods}
        selectedPaymentMethod={billingPayment.payment_method}
        selectedPaymentProfileId={billingPayment.payment_profile_id}
        showAutopayOption={true}
      />
    </div>
  }/>;
};

const mapDucksToProps = (ownProps) => ({
  currentUser,
  currentCustomer,
  billingAccount: singleDuck(billingAccounts, ownProps.params.billing_account_id),
  paymentProfiles: dependentDuck(currentUser, ({value: currentUser}) =>
    filterDuck(paymentProfiles, {user_id: currentUser.id})
  ),
  billingPayment: billingPayments.byBillingAccount(ownProps.params.billing_account_id)
});

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch, ownProps) => ({
  updatePaymentMethod: (billingPayment) => dispatch(billingPayments.actions.update(billingPayment))
});


export default {
  body: duckify(mapDucksToProps, mapStateToProps, mapDispatchToProps)(BillingAccount),
  sideBar: () => <SideBar>
    <SchoolContactInfo/>
  </SideBar>
};
